import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient, formatApiError } from "@/lib/api";
import { formatGNF } from "@/utils/format";
import { TrendingUp, Wallet, AlertOctagon, Copy, Check, Loader2, HandCoins, Clock, Smartphone, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const MIN_PARTIAL = 50000;
const POLL_MS = 5000;
const POLL_TIMEOUT_MS = 120000;

/**
 * Real-time balance card for a driver.
 * Shows: gains de la semaine, cash à reverser, solde net.
 * Debt regularisation: 1) "Régulariser maintenant via MoMo" (request-to-pay,
 * confirmed by PIN on the driver's phone, polled every 5 s, 2 min timeout),
 * 2) fallback: manual transfer declaration validated by the admin.
 */
export default function DriverBalanceCard({ compact = false, refreshKey = 0 }) {
  const { t } = useTranslation();
  const [bal, setBal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [lastRem, setLastRem] = useState(null);
  const [declaring, setDeclaring] = useState(false);
  const [amount, setAmount] = useState("");
  const [momoRef, setMomoRef] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [momoState, setMomoState] = useState("idle"); // idle | waiting | success | failed
  const [momoError, setMomoError] = useState("");
  const [partialOpen, setPartialOpen] = useState(false);
  const [partialAmount, setPartialAmount] = useState("");
  const pollTimerRef = useRef(null);

  useEffect(() => () => clearInterval(pollTimerRef.current), []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/my/ledger/balance");
      setBal(data);
      const { data: rems } = await apiClient.get("/my/ledger/remittances");
      setLastRem(rems && rems.length > 0 ? rems[0] : null);
    } catch (e) {
      // Silent — driver may not have entries yet
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh, refreshKey]);

  if (loading && !bal) {
    return (
      <div className="bg-white border border-sand rounded-2xl p-4 flex items-center gap-2 text-mute text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> {t("rdash.ddBalLoading")}
      </div>
    );
  }
  if (!bal) return null;

  const gains = bal.gains_semaine || 0;
  const cash = bal.cash_a_reverser || 0;
  const net = bal.solde_net || 0;
  const netPositive = net >= 0;
  const blocked = !!bal.blocked;
  // Dette = cash dû à NelDaari uniquement (le gain du livreur n'est jamais « à reverser »)
  const debt = cash;
  const pendingRem = lastRem && lastRem.status === "en_attente" ? lastRem : null;

  const copyRef = () => {
    if (!bal.momo_regularisation_ref) return;
    navigator.clipboard.writeText(bal.momo_regularisation_ref);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
    toast.success(t("ddrv.dbcRefCopied"));
  };

  const submitRemittance = async () => {
    const amt = parseInt(amount, 10);
    if (!amt || amt <= 0) {
      toast.error(t("ddrv.dbcInvalidAmount"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await apiClient.post("/my/ledger/remittance", { amount: amt, momo_ref: momoRef });
      setLastRem(data);
      setDeclaring(false);
      setMomoRef("");
      toast.success(t("ddrv.dbcDeclSent"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const startMomoRegularisation = async (amt) => {
    setMomoError("");
    setMomoState("waiting");
    try {
      const { data } = await apiClient.post("/my/ledger/momo-regularisation", amt ? { amount: amt } : {});
      const startedAt = Date.now();
      pollTimerRef.current = setInterval(async () => {
        try {
          const { data: reg } = await apiClient.get(`/my/ledger/momo-regularisation/${data.reg_id}`);
          if (reg.status === "successful") {
            clearInterval(pollTimerRef.current);
            setMomoState("success");
            setPartialOpen(false);
            toast.success(t("ddrv.dbcDebtSettled"));
            refresh();
          } else if (reg.status === "failed") {
            clearInterval(pollTimerRef.current);
            setMomoState("failed");
            setMomoError(t("ddrv.dbcMomoRefused"));
          } else if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            clearInterval(pollTimerRef.current);
            setMomoState("failed");
            setMomoError(t("ddrv.dbcMomoExpired"));
          }
        } catch {
          // transient poll error: keep waiting until timeout
          if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            clearInterval(pollTimerRef.current);
            setMomoState("failed");
            setMomoError(t("ddrv.dbcMomoTimeout"));
          }
        }
      }, POLL_MS);
    } catch (err) {
      setMomoState("failed");
      setMomoError(formatApiError(err));
    }
  };

  return (
    <div className="space-y-3" data-testid="driver-balance-card">
      {blocked && (
        <div className="rounded-2xl border-2 border-brand-red bg-[#FFF5F6] p-4" data-testid="driver-debt-block">
          <div className="flex items-start gap-2 mb-2">
            <AlertOctagon className="w-5 h-5 text-brand-red shrink-0" />
            <div>
              <div className="font-display font-semibold text-brand-red">{t("rdash.ddAccessSuspended")}</div>
              <div className="text-sm text-mute">
                {t("ddrv.dbcDebtOver", { amount: formatGNF(bal.debt_threshold) })}
              </div>
            </div>
          </div>
          <div className="rounded-xl bg-white border border-sand p-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-mute font-semibold">{t("ddrv.dbcMomoRef")}</div>
              <div className="font-mono text-sm font-semibold truncate">{bal.momo_regularisation_ref}</div>
            </div>
            <button
              onClick={copyRef}
              data-testid="driver-debt-copy-ref"
              className="rounded-full bg-brand-red text-white px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? t("ddrv.dbcCopied") : t("ddrv.dbcCopy")}
            </button>
          </div>
          <div className="text-xs text-mute mt-2">
            {t("ddrv.dbcTransferHint1")} <b className="text-ink">{formatGNF(debt)}</b> {t("ddrv.dbcTransferHint2")}
          </div>
        </div>
      )}

      <div className={`grid ${compact ? "grid-cols-3 gap-2" : "grid-cols-1 sm:grid-cols-3 gap-3"}`}>
        <div className="rounded-xl bg-[#E7F6EE] border border-brand-green/30 p-3" data-testid="bal-gains">
          <div className="text-[10px] uppercase tracking-widest text-mute font-semibold">{t("rdash.ddGainsWeek")}</div>
          <div className="font-display text-lg font-bold text-brand-green flex items-center gap-1">
            <TrendingUp className="w-4 h-4" /> {formatGNF(gains)}
          </div>
        </div>
        <div className="rounded-xl bg-[#FFF5F6] border border-brand-red/20 p-3" data-testid="bal-cash">
          <div className="text-[10px] uppercase tracking-widest text-mute font-semibold">{t("rdash.ddCashToRemit")}</div>
          <div className="font-display text-lg font-bold text-brand-red flex items-center gap-1">
            <Wallet className="w-4 h-4" /> {formatGNF(cash)}
          </div>
        </div>
        <div className={`rounded-xl p-3 border ${netPositive ? "bg-white border-sand" : "bg-[#FFF5F6] border-brand-red/40"}`} data-testid="bal-net">
          <div className="text-[10px] uppercase tracking-widest text-mute font-semibold">{t("rdash.ddNetBalance")}</div>
          <div className={`font-display text-lg font-bold ${netPositive ? "text-ink" : "text-brand-red"}`}>
            {net < 0 ? "− " : ""}{formatGNF(Math.abs(net))}
          </div>
        </div>
      </div>

      {/* ---- Régularisation MoMo (request-to-pay) : bouton principal ---- */}
      {debt > 0 && momoState === "waiting" && (
        <div className="rounded-2xl border border-brand-yellow bg-brand-yellow/10 p-4 flex items-center gap-3" data-testid="driver-momo-waiting">
          <Loader2 className="w-5 h-5 animate-spin text-ink shrink-0" />
          <div className="text-sm">
            <div className="font-semibold">{t("ddrv.dbcMomoWaitTitle")}</div>
            <div className="text-xs text-mute">{t("ddrv.dbcMomoWaitSub")}</div>
          </div>
        </div>
      )}
      {momoState === "success" && debt <= 0 && (
        <div className="rounded-2xl border border-brand-green bg-[#E7F6EE] p-4 flex items-center gap-2 text-sm font-semibold text-brand-green" data-testid="driver-momo-success">
          <CheckCircle2 className="w-5 h-5" /> {t("ddrv.dbcDebtSettled")}
        </div>
      )}
      {debt > 0 && momoState !== "waiting" && !pendingRem && (
        <div className="space-y-2">
          {momoState === "failed" && momoError && (
            <div className="rounded-xl border border-brand-red/40 bg-[#FFF5F6] p-3 text-xs text-brand-red" data-testid="driver-momo-failed">
              {momoError}
            </div>
          )}
          <button
            onClick={() => startMomoRegularisation(null)}
            data-testid="driver-momo-regularise"
            className="w-full rounded-xl bg-ink text-white py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
          >
            <Smartphone className="w-4 h-4" /> {t("ddrv.dbcRegulariseNow", { amount: formatGNF(debt) })}
          </button>
          {!partialOpen ? (
            <button
              onClick={() => { setPartialAmount(String(Math.min(debt, Math.max(MIN_PARTIAL, Math.round(debt / 2))))); setPartialOpen(true); }}
              data-testid="driver-momo-partial-toggle"
              className="w-full text-center text-xs font-semibold text-mute underline underline-offset-2"
            >
              {t("ddrv.dbcPayPartial")}
            </button>
          ) : (
            <div className="rounded-xl border border-sand bg-white p-3 space-y-2" data-testid="driver-momo-partial-form">
              <label className="text-xs font-semibold text-mute">{t("ddrv.dbcPartialLabel", { min: formatGNF(MIN_PARTIAL), max: formatGNF(debt) })}</label>
              <input
                type="number"
                value={partialAmount}
                onChange={(e) => setPartialAmount(e.target.value)}
                min={MIN_PARTIAL}
                max={debt}
                data-testid="driver-momo-partial-amount"
                className="w-full rounded-xl border border-sand px-3 py-2.5 text-sm font-semibold focus:outline-none focus:border-brand-green"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const amt = parseInt(partialAmount, 10);
                    if (!amt || (amt < MIN_PARTIAL && amt !== debt) || amt > debt) {
                      toast.error(t("ddrv.dbcAmountBetween", { min: formatGNF(MIN_PARTIAL), max: formatGNF(debt) }));
                      return;
                    }
                    startMomoRegularisation(amt);
                  }}
                  data-testid="driver-momo-partial-submit"
                  className="flex-1 rounded-xl bg-ink text-white py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2"
                >
                  <Smartphone className="w-4 h-4" /> {t("ddrv.dbcPayThis")}
                </button>
                <button onClick={() => setPartialOpen(false)} className="rounded-xl border border-sand px-4 py-2.5 text-sm font-semibold text-mute">
                  {t("ddrv.dbcCancel")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ---- Secours : virement manuel + déclaration validée par l'admin ---- */}
      {pendingRem && (
        <div className="rounded-xl border border-brand-yellow bg-brand-yellow/10 p-3 flex items-center gap-2 text-sm" data-testid="driver-remittance-pending">
          <Clock className="w-4 h-4 text-ink shrink-0" />
          <div>
            {t("ddrv.dbcPendingDecl1")} <b>{formatGNF(pendingRem.amount)}</b> {t("ddrv.dbcPendingDecl2")}
          </div>
        </div>
      )}
      {!pendingRem && lastRem?.status === "rejete" && debt > 0 && (
        <div className="rounded-xl border border-brand-red/40 bg-[#FFF5F6] p-3 text-xs text-brand-red" data-testid="driver-remittance-rejected">
          {t("ddrv.dbcRejected", { note: lastRem.reject_note ? ` : ${lastRem.reject_note}` : "" })}
        </div>
      )}
      {debt > 0 && !pendingRem && !declaring && momoState !== "waiting" && (
        <button
          onClick={() => { setAmount(String(debt)); setDeclaring(true); }}
          data-testid="driver-declare-remittance"
          className="w-full rounded-xl border-2 border-brand-green text-brand-green bg-white py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
        >
          <HandCoins className="w-4 h-4" /> {t("ddrv.dbcAlreadyPaid")}
        </button>
      )}
      {debt > 0 && !pendingRem && declaring && (
        <div className="rounded-2xl border border-sand bg-white p-4 space-y-3" data-testid="driver-remittance-form">
          <div className="font-semibold text-sm flex items-center gap-1.5">
            <HandCoins className="w-4 h-4 text-brand-green" /> {t("ddrv.dbcDeclareTitle")}
          </div>
          <div className="rounded-xl bg-elevated p-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-mute font-semibold">{t("ddrv.dbcMomoRefToUse")}</div>
              <div className="font-mono text-sm font-semibold truncate">{bal.momo_regularisation_ref}</div>
            </div>
            <button onClick={copyRef} data-testid="driver-remittance-copy-ref" className="rounded-full bg-ink text-white px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 shrink-0">
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? t("ddrv.dbcCopied") : t("ddrv.dbcCopy")}
            </button>
          </div>
          <div>
            <label className="text-xs font-semibold text-mute">{t("ddrv.dbcAmountLabel")}</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="driver-remittance-amount"
              className="mt-1 w-full rounded-xl border border-sand px-3 py-2.5 text-sm font-semibold focus:outline-none focus:border-brand-green"
            />
            <div className="text-[11px] text-mute mt-0.5">{t("ddrv.dbcCurrentDebt", { amount: formatGNF(debt) })}</div>
          </div>
          <div>
            <label className="text-xs font-semibold text-mute">{t("ddrv.dbcMomoTxRef")}</label>
            <input
              type="text"
              value={momoRef}
              onChange={(e) => setMomoRef(e.target.value)}
              placeholder="Ex : MP240708.1234.A12345"
              data-testid="driver-remittance-momo-ref"
              className="mt-1 w-full rounded-xl border border-sand px-3 py-2.5 text-sm focus:outline-none focus:border-brand-green"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={submitRemittance}
              disabled={submitting}
              data-testid="driver-remittance-submit"
              className="flex-1 rounded-xl bg-brand-green text-white py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {t("ddrv.dbcSendDecl")}
            </button>
            <button
              onClick={() => setDeclaring(false)}
              data-testid="driver-remittance-cancel"
              className="rounded-xl border border-sand px-4 py-2.5 text-sm font-semibold text-mute"
            >
              {t("ddrv.dbcCancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
